import { get } from "idb-keyval";

import type { ChatThread } from "types/ChatThread";
import type { Message } from "types/Message";
import { PRODUCT_FILE_PREFIX } from "utilities/productName";
import saveBlob from "utilities/saveBlob";

export interface LegacyIdbSnapshot {
  /**
   * `Record<id, ChatThread>` as written by the pre-API `useChatThreads`
   * IndexedDB persistence.
   */
  chatThreads: Record<string, ChatThread>;
  /**
   * Each entry's key is `messages-<threadId>`. Threads with no messages
   * key in IDB are absent here; the migration upload defaults to `[]` for
   * those.
   */
  messagesByKey: Record<string, Message[]>;
}

/**
 * Reads the legacy IndexedDB chat data. Returns null when there is nothing
 * to migrate (no `chatThreads` key, or the key holds an empty object).
 */
export async function readLegacyIdb(): Promise<LegacyIdbSnapshot | null> {
  let chatThreads: Record<string, ChatThread> | undefined;
  try {
    chatThreads = await get<Record<string, ChatThread>>("chatThreads");
  } catch (error) {
    console.warn("Failed to read legacy chatThreads from IndexedDB:", error);
    return null;
  }

  if (!chatThreads || Object.keys(chatThreads).length === 0) {
    return null;
  }

  const messagesByKey: Record<string, Message[]> = {};
  for (const id of Object.keys(chatThreads)) {
    const key = `messages-${id}`;
    try {
      const messages = await get<Message[]>(key);
      if (Array.isArray(messages)) {
        messagesByKey[key] = messages;
      }
    } catch (error) {
      console.warn(`Failed to read ${key} from IndexedDB:`, error);
    }
  }

  return { chatThreads, messagesByKey };
}

/**
 * Serializes a legacy IDB snapshot to a JSON Blob wrapped in a File so it's
 * a user-facing backup matching the legacy export shape.
 *
 * Falls back to a piecewise build on `RangeError` from `JSON.stringify`,
 * which V8 throws when the serialized payload would exceed its
 * max-string-length (~512 MB). The fallback emits the same JSON shape
 * byte-for-byte via many small stringify calls.
 */
export function buildLegacyExportFile(snapshot: LegacyIdbSnapshot): File {
  const payload = {
    chatThreads: snapshot.chatThreads,
    ...snapshot.messagesByKey,
  };
  let parts: BlobPart[];
  try {
    parts = [JSON.stringify(payload, null, 2)];
  } catch (error) {
    if (!(error instanceof RangeError)) throw error;
    parts = [
      '{\n  "chatThreads": ',
      JSON.stringify(snapshot.chatThreads, null, 2),
    ];
    for (const [key, messages] of Object.entries(snapshot.messagesByKey)) {
      parts.push(
        ",\n  ",
        JSON.stringify(key),
        ": ",
        JSON.stringify(messages, null, 2)
      );
    }
    parts.push("\n}\n");
  }
  const blob = new Blob(parts, { type: "application/json" });
  return new File([blob], "idb-migration.json", { type: "application/json" });
}

/** Downloads a legacy IDB snapshot as a JSON file. */
export function downloadLegacyExport(snapshot: LegacyIdbSnapshot): void {
  const file = buildLegacyExportFile(snapshot);
  const date = new Date().toISOString().split("T")[0];
  saveBlob(file, `${PRODUCT_FILE_PREFIX}-legacy-${date}.json`);
}
